import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import { Button, Card, Input, Segment } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { authApi } from '../api';

const FEATURES = [
  ['Digital authorization', 'Dealers apply with a Company ID; manufacturers approve in one tap.'],
  ['Live inventory & orders', 'Stock reserves on acceptance and issues on dispatch — automatically.'],
  ['Contracts & GST billing', 'Fixed-price agreements, digital acceptance and compliant invoices.'],
  ['Keys you control', 'Razorpay, WhatsApp and OTP mode are configured right here, no redeploy.'],
];

/** Six-box OTP entry with paste + arrow-key support. */
const OtpBoxes = ({ value, onChange, length = 6, onComplete }) => {
  const refs = useRef([]);

  const setDigit = (index, digit) => {
    const next = value.split('');
    next[index] = digit;
    const joined = next.join('').slice(0, length);
    onChange(joined);

    if (digit && index < length - 1) refs.current[index + 1]?.focus();
    if (joined.length === length && !joined.includes('') && onComplete) onComplete(joined);
  };

  const onKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) refs.current[index - 1]?.focus();
    if (e.key === 'ArrowLeft' && index > 0) refs.current[index - 1]?.focus();
    if (e.key === 'ArrowRight' && index < length - 1) refs.current[index + 1]?.focus();
  };

  const onPaste = e => {
    const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!digits) return;
    e.preventDefault();
    onChange(digits);
    refs.current[Math.min(digits.length, length - 1)]?.focus();
    if (digits.length === length && onComplete) onComplete(digits);
  };

  return (
    <div className="otp-inputs" onPaste={onPaste}>
      {Array.from({ length }).map((unused, index) => (
        <input
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          ref={el => {
            refs.current[index] = el;
          }}
          className={value[index] ? 'filled' : ''}
          inputMode="numeric"
          maxLength={1}
          value={value[index] || ''}
          onChange={e => setDigit(index, e.target.value.replace(/\D/g, ''))}
          onKeyDown={e => onKeyDown(index, e)}
        />
      ))}
    </div>
  );
};

const Login = () => {
  const { user, loginWithPassword, loginWithOtp } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [mode, setMode] = useState('password');
  const [email, setEmail] = useState('admin@tarpex.live');
  const [password, setPassword] = useState('');
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [devOtp, setDevOtp] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [busy, setBusy] = useState(false);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    authApi.config().then(res => setConfig(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  if (user) return <Navigate to="/admin" replace />;

  const finish = () => {
    toast.success('Welcome back');
    navigate('/admin', { replace: true });
  };

  const submitPassword = async e => {
    e.preventDefault();
    if (!email || !password) return toast.error('Enter your email and password');

    setBusy(true);
    try {
      await loginWithPassword(email.trim(), password);
      finish();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  const sendOtp = async () => {
    if (!/^[6-9]\d{9}$/.test(mobile)) return toast.error('Enter a valid 10-digit mobile number');

    setBusy(true);
    try {
      const res = await authApi.requestOtp(mobile);
      setOtpSent(true);
      setDevOtp(res.data.devOtp || '');
      setCooldown(res.data.resendAfterSeconds || 30);
      toast.success(res.message);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  const verifyOtp = async (code = otp) => {
    if (code.length < 6) return toast.error('Enter the 6-digit OTP');

    setBusy(true);
    try {
      await loginWithOtp(mobile, code);
      finish();
    } catch (err) {
      toast.error(err.message);
      setOtp('');
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  return (
    <div className="login-wrap">
      <div className="login-art">
        <Logo size={34} variant="light" />

        <div className="pitch">
          <h1>The tarpaulin trade, running on one network.</h1>
          <p>
            Manufacturers, distributors, dealers, logistics, insurance and finance partners —
            connected through a single verified supply chain.
          </p>

          <div className="login-features">
            {FEATURES.map(([title, body]) => (
              <div className="login-feature" key={title}>
                <span className="chk">✓</span>
                <div>
                  <b>{title}</b>
                  {body}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ position: 'relative', zIndex: 1, color: 'rgba(255,255,255,.4)', fontSize: 12 }}>
          © {new Date().getFullYear()} Tarpex.live · Built by Stackerio
        </div>
      </div>

      <div className="login-panel">
        <div className="login-card">
          <div style={{ marginBottom: 22 }}>
            <Logo size={32} />
          </div>

          <span className="login-badge">◈ Super Admin Console</span>
          <h2 style={{ fontSize: 25 }}>Sign in to your panel</h2>
          <p className="text-muted mb-2" style={{ marginTop: 4 }}>
            Manage the entire Tarpex supply chain network.
          </p>

          <Card bodyClass="">
            <Segment
              value={mode}
              onChange={m => {
                setMode(m);
                setOtpSent(false);
                setOtp('');
              }}
              options={[
                { value: 'password', label: 'Email & password' },
                { value: 'otp', label: 'Mobile OTP' },
              ]}
            />

            <div className="mt-2">
              {mode === 'password' ? (
                <form onSubmit={submitPassword}>
                  <Input
                    label="Email address"
                    type="email"
                    lead="✉"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="admin@tarpex.live"
                    autoComplete="username"
                  />
                  <Input
                    label="Password"
                    type="password"
                    lead="🔒"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                  <Button type="submit" block size="lg" loading={busy} onClick={submitPassword}>
                    Sign in
                  </Button>
                </form>
              ) : !otpSent ? (
                <>
                  <Input
                    label="Mobile number"
                    lead="+91"
                    inputMode="numeric"
                    maxLength={10}
                    value={mobile}
                    onChange={e => setMobile(e.target.value.replace(/\D/g, ''))}
                    placeholder="9999999999"
                    hint="We'll send a one-time password to this number."
                  />
                  <Button block size="lg" loading={busy} onClick={sendOtp}>
                    Send OTP
                  </Button>
                </>
              ) : (
                <>
                  <div className="field">
                    <label>Enter the 6-digit code</label>
                    <OtpBoxes value={otp} onChange={setOtp} onComplete={verifyOtp} />
                    <div className="hint">
                      Sent to +91 {mobile}.{' '}
                      <button
                        type="button"
                        onClick={() => setOtpSent(false)}
                        style={{ background: 'none', border: 'none', color: 'var(--green-600)', cursor: 'pointer', padding: 0, fontWeight: 600 }}
                      >
                        Change
                      </button>
                    </div>
                  </div>

                  <Button block size="lg" loading={busy} onClick={() => verifyOtp()}>
                    Verify & sign in
                  </Button>

                  <Button
                    block
                    variant="ghost"
                    className="mt-1"
                    disabled={cooldown > 0}
                    onClick={sendOtp}
                  >
                    {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend OTP'}
                  </Button>

                  {devOtp && (
                    <div className="dev-hint">
                      <strong>Development mode.</strong> No SMS is sent — use OTP <strong>{devOtp}</strong>.
                      Switch to live mode in Settings → OTP once your SMS gateway keys are saved.
                    </div>
                  )}
                </>
              )}
            </div>
          </Card>

          {config && config.maintenanceMode && (
            <div className="dev-hint mt-2">
              <strong>Maintenance mode is on.</strong> {config.maintenanceMessage}
            </div>
          )}

          <p className="text-muted text-sm mt-2" style={{ textAlign: 'center' }}>
            Restricted to Super Admin accounts.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
